/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';

import CurrencyTable from './CurrencyTable/CurrencyTable';
import Evolution from '../../components/Evolution/Evolution';
import LineChart from '../../components/LineChart/LineChart';
import Loader from '../../components/Loader/Loader';
import Asterix, { AsterixType } from '../../components/Asterix/Asterix';

import CurrencyService from '../../core/services/currency-service';
import CommentsApiService from '../../core/services/comments-api-service';
import currencyEvolution from '../../core/data/currency/currency-evolution';
import COLORS from '../../core/constants/colors';
import { CurrencyModel } from '../../core/models/currency';

import './CurrencySection.scss';
import ChartHints from '../../components/ChartHints/ChartHints';

const apiService = new CurrencyService();
const commentsService = new CommentsApiService('');

export default function CurrencySection(): JSX.Element {
  const [currencyData, setCurrencyData] = useState<CurrencyModel>({} as CurrencyModel);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiService.getCurrency().then((response) => {
      setCurrencyData(response);
      setIsLoading(false);
    });
  }, []);

  const onSave = (data: { value: string; id: number; type: string }) => {
    const sanitizeConf = {
      allowedTags: [],
      allowedAttributes: {},
    };
    const value = sanitizeHtml(data.value, sanitizeConf);
    if (data.id) {
      return commentsService.updateCustomPrice(data.id, data.type, value);
    }
    return commentsService.setCustomPrice(data.type, value).then((response: any) => {
      setCurrencyData({
        ...currencyData,
        crtPricesTable: {
          ...currencyData.crtPricesTable,
          pricesTable: currencyData.crtPricesTable.pricesTable.map((item) => {
            if (item.postIndex === data.type) {
              return {
                ...item,
                idUpdate: response.id,
              };
            }
            return item;
          }),
        },
      });
    });
  };

  const onRemoveCustomPrice = (id: number) => {
    return commentsService.removeCustomPrice(id).then(() => {
      setCurrencyData({
        ...currencyData,
        crtPricesTable: {
          ...currencyData.crtPricesTable,
          pricesTable: currencyData.crtPricesTable.pricesTable.map((item) => {
            if (item.idUpdate === id) {
              return {
                ...item,
                idUpdate: null,
                col4: Number(item.col4),
              };
            }
            return item;
          }),
        },
      });
    });
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div className="CurrencySection" id="currency">
      <div className="total-container">
        {/* <img src={currencyIcon} alt="" /> */}
        <h2>
          <span>CURRENCY</span>
        </h2>
        <div className="CurrencySection__content">
          <div className="row">
            <div className="col-lg-6 mb-4">
              <div className="CurrencySection__evolution fsc flex-wrap">
                <div className="mb-3">
                  <Evolution
                    arrow={currencyData.crt1.arrow}
                    price={currencyData.crt1.value}
                    origin={currencyEvolution.euro.origin}
                    currency={currencyEvolution.euro.currency}
                    decimals={4}
                  />
                </div>

                <div>
                  <Evolution
                    arrow={currencyData.crt2.arrow}
                    price={currencyData.crt2.value}
                    origin={currencyEvolution.dollar.origin}
                    currency={currencyEvolution.dollar.currency}
                    decimals={4}
                  />
                </div>
              </div>
              <Asterix type={AsterixType.alter} />
            </div>

            <div className="col-lg-6 mb-4">
              <CurrencyTable
                data={{ ...currencyData.crtPricesTable }}
                onEditCell={onSave}
                onRemoveCustomPrice={onRemoveCustomPrice}
              />
            </div>
          </div>

          <div className="row mb-10">
            <div className="col-lg-12">
              <LineChart
                title="Currency Movement Last 12 Months"
                yAxisTitle="£/€"
                colors={[COLORS.red, COLORS.blueChart]}
                data={[...currencyData.chartCrt]}
              />
              <ChartHints />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
